import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, Gift } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Register() {
    const { t } = useI18n();
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [ref, setRef] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fromUrl = (params.get('ref') || '').trim().toUpperCase();
        const stored = (localStorage.getItem('growdaily_ref') || '').trim().toUpperCase();
        const code = fromUrl || stored;
        if (code) {
            setRef(code);
            try { localStorage.setItem('growdaily_ref', code); } catch {}
        }
    }, [params]);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
        setBusy(true);
        try {
            const r = await api.post('/auth/register', { name, email, password, ref: ref || undefined });
            if (r.data?.session_token) localStorage.setItem('session_token', r.data.session_token);
            setUser(r.data.user);
            try { localStorage.removeItem('growdaily_ref'); } catch {}
            const next = params.get('next');
            // Same-site paths only: reject //host and /\\host, which browsers treat as another site
            window.location.replace(next && /^\/(?![\/\\])/.test(next) ? next : '/dashboard');
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === 'string' ? d : 'Could not create account');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-brand-bg botanical-noise px-6 py-12 sm:py-20">
            <LanguageSwitcher variant="floating" />
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-11 h-11 rounded-2xl bg-brand-green flex items-center justify-center overflow-hidden shadow-sm">
                        <img src="/growdaily-mark.png" alt="GrowDaily" className="w-8 h-8 object-contain" />
                    </div>
                    <div className="font-serif text-lg text-brand-ink">GrowDaily</div>
                </div>

                <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink leading-tight">{t('register.title')}</h1>
                <p className="text-brand-ink2 mt-2 mb-8">{t('register.sub')}</p>

                {ref && (
                    <div data-testid="reg-ref-banner" className="mb-4 flex items-start gap-2 text-sm text-brand-green bg-brand-green/10 border border-brand-green/30 rounded-2xl p-3">
                        <Gift size={16} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                        <span>You're invited with code <strong>{ref}</strong> — you both get 1 month free Premium 🎁</span>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-3">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('register.name')}</span>
                        <div className="mt-1 relative">
                            <User size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                            <input data-testid="input-reg-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-brand-card border border-brand-border rounded-full pl-11 pr-4 py-3" placeholder="Anna Jensen" />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Email</span>
                        <div className="mt-1 relative">
                            <Mail size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                            <input data-testid="input-reg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-brand-card border border-brand-border rounded-full pl-11 pr-4 py-3" placeholder="dig@email.dk" />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">{t('register.password')}</span>
                        <div className="mt-1 relative">
                            <Lock size={15} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-ink3" />
                            <input data-testid="input-reg-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-brand-card border border-brand-border rounded-full pl-11 pr-4 py-3" placeholder={t('register.min')} />
                        </div>
                    </label>

                    {error && (
                        <div data-testid="reg-error" className="flex items-start gap-2 text-sm text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/20 rounded-2xl p-3">
                            <AlertCircle size={16} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}

                    <button data-testid="btn-submit-register" disabled={busy} type="submit" className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50">
                        {busy ? 'Opretter…' : t('register.submit')}
                    </button>
                </form>

                <p className="text-sm text-brand-ink3 mt-6 text-center">
                    {t('register.have')} <Link to="/login" className="text-brand-green font-medium hover:text-brand-greenDark">{t('login.submit')}</Link>
                </p>
            </div>
        </div>
    );
}
